import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Coins } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [billing, setBilling] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      if (attempts.current >= 6) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          const b = await api.get("/billing");
          setBilling(b.data);
          setState("success");
          return;
        }
        if (r.data.status === "failed") { setState("failed"); return; }
      } catch (e) { console.error("payment status poll error (retrying):", e); }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="bg-surface border border-white/10 rounded-md p-10 max-w-md w-full text-center" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 text-brand animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-2">Confirming your payment…</h1>
            <p className="text-white/50 text-sm">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-signal-green mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-2">You're all set! 🎉</h1>
            {billing && (
              <div className="flex items-center justify-center gap-2 my-4 text-signal-yellow" data-testid="new-balance">
                <Coins className="w-5 h-5" />
                <span className="font-mono text-2xl">{billing.pro_active ? "Pro · Unlimited" : `${billing.credits} credits`}</span>
              </div>
            )}
            <p className="text-white/50 text-sm mb-6">Your account has been upgraded. Time to make some videos.</p>
            <button data-testid="go-studio" onClick={() => nav("/studio")} className="bg-brand hover:bg-brand-hover text-white rounded-md px-6 py-2.5 text-sm font-medium transition-colors">Go to Content Studio</button>
          </>
        )}
        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle className="w-12 h-12 text-brand mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-2">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
            <p className="text-white/50 text-sm mb-6">{state === "timeout" ? "Your payment is taking longer than usual. Your credits will appear once it settles." : "No charge was made. You can try again anytime."}</p>
            <Link to="/pricing" data-testid="back-pricing" className="bg-white/10 hover:bg-white/15 rounded-md px-6 py-2.5 text-sm font-medium transition-colors inline-block">Back to Pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}
